import type { CasaLeonTableVisual } from '@casa-leon-vivo/contracts/casa-leon';

export interface SectorGrid {
  sector: 'salon' | 'patio';
  cols: number;
  rows: number;
}

const SALON: SectorGrid = { sector: 'salon', cols: 8, rows: 6 };
const PATIO: SectorGrid = { sector: 'patio', cols: 6, rows: 5 };

function footprintForParty(partySize: number): { cols: number; rows: number; aggregate: boolean } {
  if (partySize <= 4) return { cols: 1, rows: 1, aggregate: false };
  if (partySize <= 8) return { cols: 2, rows: 1, aggregate: false };
  if (partySize <= 12) return { cols: 2, rows: 2, aggregate: false };
  return { cols: 3, rows: 2, aggregate: true };
}

function collides(
  a: { col: number; row: number; cols: number; rows: number },
  b: { col: number; row: number; cols: number; rows: number },
): boolean {
  return (
    a.col < b.col + b.cols &&
    a.col + a.cols > b.col &&
    a.row < b.row + b.rows &&
    a.row + a.rows > b.row
  );
}

function findSlot(
  grid: SectorGrid,
  footprint: { cols: number; rows: number },
  occupied: CasaLeonTableVisual[],
): { col: number; row: number } | null {
  for (let row = 0; row <= grid.rows - footprint.rows; row += 1) {
    for (let col = 0; col <= grid.cols - footprint.cols; col += 1) {
      const candidate = { col, row, cols: footprint.cols, rows: footprint.rows };
      const hit = occupied.some((t) =>
        t.sector === grid.sector &&
        collides(candidate, {
          col: t.slot.col,
          row: t.slot.row,
          cols: t.footprint.cols,
          rows: t.footprint.rows,
        }),
      );
      if (!hit) {
        return { col, row };
      }
    }
  }
  return null;
}

export function placeOrUpdateTable(
  tables: CasaLeonTableVisual[],
  input: {
    tableId: string;
    sector: 'salon' | 'patio';
    partySize: number;
  },
): { tables: CasaLeonTableVisual[]; error?: string } {
  const grid = input.sector === 'salon' ? SALON : PATIO;
  const footprint = footprintForParty(input.partySize);
  const existingIdx = tables.findIndex((t) => t.tableId === input.tableId);
  const others = existingIdx >= 0 ? tables.filter((t) => t.tableId !== input.tableId) : tables;

  const slot =
    existingIdx >= 0
      ? tables[existingIdx].slot
      : findSlot(grid, footprint, others.filter((t) => t.sector === input.sector));

  if (!slot) {
    return { tables, error: 'sin_espacio_en_sector' };
  }

  const next: CasaLeonTableVisual = {
    tableId: input.tableId,
    sector: input.sector,
    partySize: input.partySize,
    operationalState: existingIdx >= 0 ? tables[existingIdx].operationalState : 'abierta',
    staffId: existingIdx >= 0 ? tables[existingIdx].staffId : undefined,
    consumptionTotal: existingIdx >= 0 ? tables[existingIdx].consumptionTotal : undefined,
    slot,
    footprint,
    aggregate: footprint.aggregate,
  };

  const merged = [...others];
  if (existingIdx >= 0) {
    merged.splice(existingIdx, 0, next);
  } else {
    merged.push(next);
  }
  return { tables: merged };
}

export function releaseTable(tables: CasaLeonTableVisual[], tableId: string): CasaLeonTableVisual[] {
  return tables.filter((t) => t.tableId !== tableId);
}
